<template>
	<div>
		<!-- Leitura exibida + posição inicial -->
		<div class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-mb-4">
			<div class="tw-text-sm" :class="theme === 'dark' ? 'tw-text-slate-400' : 'tw-text-slate-500'">
				<template v-if="leituraInfo.carregando">
					<i class="fa-solid fa-circle-notch fa-spin tw-mr-1.5"></i> Buscando a última leitura completa...
				</template>
				<template v-else-if="leituraInfo.data">
					Exibindo leitura{{ leituraInfo.completa ? '' : ' (incompleta)' }}:
					<span class="tw-font-semibold" :class="theme === 'dark' ? 'tw-text-slate-200' : 'tw-text-slate-700'">{{ leituraInfo.data }}</span>
				</template>
				<template v-else>Nenhuma leitura disponível</template>
			</div>
			<button
				type="button"
				@click="resetSilo"
				class="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-rounded-lg tw-border tw-text-sm tw-font-medium"
				:class="theme === 'dark' ? 'tw-border-slate-700 tw-text-slate-300 hover:tw-bg-slate-800' : 'tw-border-slate-300 tw-text-slate-600 hover:tw-bg-slate-100'"
			>
				<i class="fa-solid fa-rotate-left"></i> Posição inicial
			</button>
		</div>

		<!-- Elementos visíveis -->
		<div class="tw-flex tw-flex-wrap tw-gap-2 tw-mb-4">
			<button
				v-for="chip in chips"
				:key="chip.id"
				type="button"
				:aria-pressed="chip.ativo"
				@click="chip.alternar"
				class="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1 tw-rounded-full tw-border tw-text-xs tw-font-semibold tw-transition-colors"
				:class="chip.ativo
					? (theme === 'dark' ? 'tw-bg-emerald-600/15 tw-border-emerald-600/50 tw-text-emerald-400' : 'tw-bg-emerald-50 tw-border-emerald-300 tw-text-emerald-700')
					: (theme === 'dark' ? 'tw-border-slate-700 tw-text-slate-500 hover:tw-text-slate-300' : 'tw-border-slate-300 tw-text-slate-400 hover:tw-text-slate-600')"
			>
				<i class="fa-solid tw-text-[10px]" :class="chip.ativo ? 'fa-eye' : 'fa-eye-slash'"></i>
				{{ chip.label }}
			</button>
		</div>

		<div class="tw-relative tw-rounded-2xl tw-overflow-hidden tw-shadow-lg tw-w-full" :style="wrapperStyle">
			<canvas id="scene" ref="scene"></canvas>
			<div
				class="tw-hidden md:tw-block tw-absolute tw-left-3 tw-bottom-3 tw-text-xs tw-px-2.5 tw-py-1.5 tw-rounded-lg tw-border tw-pointer-events-none"
				:class="theme === 'dark' ? 'tw-bg-slate-800/80 tw-text-slate-400 tw-border-slate-700' : 'tw-bg-white/80 tw-text-slate-500 tw-border-slate-200'"
			>
				Arraste para girar · scroll para aproximar · botão direito para mover
			</div>
		</div>
	</div>
</template>

<script>
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';
import { useToast } from 'vue-toastification';
import temperaturaCores from "@/assets/colors.json";

// Dimensões do silo (unidades da cena).
const R = 1.7;                 // raio do corpo
const Y_BASE = -1;             // topo da fundação / fundo do silo
const CORPO_H = 2;             // altura do corpo cilíndrico
const Y_BEIRAL = Y_BASE + CORPO_H;
const BEIRAL = 0.07;           // avanço do telhado além da parede
const TELHADO_H = 0.72;        // altura do telhado cônico
const R_TELHADO = R + BEIRAL;

// Raio (posição radial) dos cabos por quantidade de arcos.
const RAIO_ARCO_MAP = {
	1: [R * 0.5],
	2: [R * 0.8, R * 0.4],
	3: [R * 0.75, R * 0.5, R * 0.25],
	4: [R * 0.8, R * 0.6, R * 0.4, R * 0.2],
	5: [R * 0.831, R * 0.653, R * 0.499, R * 0.333, R * 0.166]
};

// Cores do grão.
const GRAO_FUNDO = '#8a5a17';
const GRAO_MEIO = '#c08a2e';
const GRAO_TOPO = '#e3be68';
// Ângulo de repouso do produto (graus): inclinação do cone no topo da massa.
const ANGULO_REPOUSO = 23;
// Margem entre o primeiro/último sensor e as pontas do cabo.
const MARGEM_CABO = 0.12;
// Quanto o último sensor de cada cabo sobe além do comprimento base.
const EXTRA_TOPO_CABO = 0.2;
// Folga mínima entre o último sensor e o telhado (o sensor nunca sai do silo).
const FOLGA_TELHADO = 0.1;
// Cor do número do cabo.
const COR_CABO = '#38bdf8';

// Gerador pseudo-aleatório determinístico (mesmo silo => mesmo relevo).
function rng(seed) {
	let s = (Math.abs(Math.floor(seed)) % 2147483646) + 1;
	return () => {
		s = (s * 16807) % 2147483647;
		return (s - 1) / 2147483646;
	};
}

export default {
	name: 'Silo3dComponent',
	props: {
		termometria: {
			type: Object,
			default: () => ({}),
		},
		silo: {
			type: Object,
			default: () => ({}),
		},
		theme: {
			type: String,
			default: 'light'
		},
		// Leitura exibida: { data, completa, carregando }.
		leituraInfo: {
			type: Object,
			default: () => ({ data: null, completa: true, carregando: false })
		}
	},
	data() {
		return {
			arcosVisiveis: {},
			arcosExistentes: [],
			caboCentralVisivel: true,
			possuiCaboCentral: false,
			nivelVisivel: true,
			valoresVisiveis: true
		};
	},
	computed: {
		wrapperStyle() {
			return this.theme === 'dark'
				? { background: 'radial-gradient(circle at 50% 28%, #232a35 0%, #12161d 68%, #0b0e13 100%)' }
				: { background: 'radial-gradient(circle at 50% 28%, #d3dbe5 0%, #a7b4c4 62%, #8e9cb0 100%)' };
		},
		chips() {
			const chips = this.arcosExistentes.map(arco => ({
				id: `arco-${arco}`,
				label: `Arco ${arco}`,
				ativo: this.arcosVisiveis[arco] !== false,
				alternar: () => { this.arcosVisiveis[arco] = this.arcosVisiveis[arco] === false; this.toggleArco(arco); }
			}));
			if (this.possuiCaboCentral) {
				chips.push({ id: 'central', label: 'Cabo central', ativo: this.caboCentralVisivel, alternar: () => { this.caboCentralVisivel = !this.caboCentralVisivel; this.toggleCaboCentral(); } });
			}
			if (this.silo.tipo === 'silo') {
				chips.push({ id: 'nivel', label: 'Produto', ativo: this.nivelVisivel, alternar: () => { this.nivelVisivel = !this.nivelVisivel; this.toggleNivel(); } });
			}
			chips.push({ id: 'valores', label: 'Temperaturas', ativo: this.valoresVisiveis, alternar: () => { this.valoresVisiveis = !this.valoresVisiveis; this.toggleValores(); } });
			return chips;
		},
		// Só os dados que afetam o modelo: evita observar o silo inteiro (leituras,
		// aeração...) e reconstruir a cena quando nada relevante mudou.
		assinatura3d() {
			const s = this.silo || {};
			return JSON.stringify([
				s.id, s.tipo, s.tipo_nivel, s.nivel_automatico, s.nivel_manual,
				(s.cabos || []).map(c => [c.cabo, c.arco, c.posicao, c.sensores]),
				(this.termometria?.dados || []).map(d => [d.cabo, d.sensores, d.temperaturas])
			]);
		}
	},
	watch: {
		assinatura3d() {
			this.scheduleRebuild();
		},
		theme() {
			this.scheduleRebuild();
		}
	},
	mounted() {
		const toast = useToast();
		try {
			this.computeArcos();
			this.initThreeJS();
		} catch (e) {
			toast.error('Erro ao gerar silo 3D');
			console.error(e);
		}
	},
	beforeUnmount() {
		if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
		if (this._resizeObserver) this._resizeObserver.disconnect();
		if (this._frameId) cancelAnimationFrame(this._frameId);
		this.cancelarAnimacao();
		this.disposeSilo();
		(this._sceneDisposables || []).forEach(d => { try { d.dispose && d.dispose(); } catch (_) { /* noop */ } });
		if (this.controls) this.controls.dispose();
		if (this.renderer) {
			this.renderer.dispose();
			if (this.renderer.forceContextLoss) this.renderer.forceContextLoss();
		}
	},
	methods: {
		// ---------------------------------------------------------------
		// Dados
		// ---------------------------------------------------------------
		computeArcos() {
			const cabos = Array.isArray(this.silo?.cabos) ? this.silo.cabos : [];
			this.arcosExistentes = [...new Set(cabos.map(c => c.arco))].filter(a => a !== 999);
			const visiveis = {};
			this.arcosExistentes.forEach(a => { visiveis[a] = true; });
			this.arcosVisiveis = visiveis;
			this.possuiCaboCentral = cabos.some(c => c.arco === 999);
		},

		nivelPercentual() {
			const v = Number(this.silo.tipo_nivel == 1 ? this.silo.nivel_automatico : this.silo.nivel_manual);
			return Number.isNaN(v) ? 0 : THREE.MathUtils.clamp(v, 0, 100);
		},

		// ---------------------------------------------------------------
		// Setup da cena (uma vez)
		// ---------------------------------------------------------------
		initThreeJS() {
			const canvas = this.$refs.scene || document.getElementById('scene');
			const container = canvas.parentElement;
			const width = container.clientWidth || 800;
			const height = canvas.clientHeight || container.clientHeight || 600;

			this.scene = new THREE.Scene();

			this.camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 100);
			this.camera.position.set(4.4, 2.5, 5.8);

			this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
			this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
			this.renderer.setSize(width, height, false);
			this.renderer.setClearColor(0x000000, 0);

			// Ambiente (gerado uma vez) para o brilho do aço galvanizado.
			const pmrem = new THREE.PMREMGenerator(this.renderer);
			const envTex = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
			this.scene.environment = envTex;
			this.scene.environmentIntensity = 0.55;
			pmrem.dispose();

			const hemi = new THREE.HemisphereLight(0xffffff, 0x4a5058, this.theme === 'dark' ? 0.9 : 1.0);
			this.scene.add(hemi);
			const key = new THREE.DirectionalLight(0xffffff, 1.25);
			key.position.set(5, 8, 6);
			this.scene.add(key);
			const fill = new THREE.DirectionalLight(0xbcd0e6, 0.4);
			fill.position.set(-6, 3, -4);
			this.scene.add(fill);

			this._sceneDisposables = [envTex];
			this.buildContactShadow();

			this.controls = new OrbitControls(this.camera, this.renderer.domElement);
			this.controls.target.set(0, 0.15, 0);
			this.controls.enableDamping = false;
			this.controls.enableZoom = true;
			this.controls.minDistance = 3;
			this.controls.maxDistance = 17;
			this.controls.minPolarAngle = 0;
			this.controls.maxPolarAngle = Math.PI / 2.05;
			this.controls.update();
			this.controls.addEventListener('change', () => this.requestRender());
			// Usuário soltou o modelo: se o silo saiu inteiro da tela, volta à posição inicial.
			this.controls.addEventListener('start', () => this.cancelarAnimacao());
			this.controls.addEventListener('end', () => this.verificarForaDaTela());

			this.buildSilo();
			// Posição inicial com a escada virada para a câmera (depende do silo construído).
			this.camera.position.copy(this.posicaoInicialCamera());
			this.controls.update();

			this._resizeHandler = () => this.scheduleResize();
			window.addEventListener('resize', this._resizeHandler);
			if (typeof ResizeObserver !== 'undefined') {
				this._resizeObserver = new ResizeObserver(() => this.scheduleResize());
				this._resizeObserver.observe(canvas);
			}

			this.requestRender();
		},

		buildContactShadow() {
			const s = 256;
			const cv = document.createElement('canvas');
			cv.width = cv.height = s;
			const ctx = cv.getContext('2d');
			const grd = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
			grd.addColorStop(0, 'rgba(0,0,0,0.4)');
			grd.addColorStop(0.6, 'rgba(0,0,0,0.14)');
			grd.addColorStop(1, 'rgba(0,0,0,0)');
			ctx.fillStyle = grd;
			ctx.fillRect(0, 0, s, s);
			const tex = new THREE.CanvasTexture(cv);
			const geo = new THREE.PlaneGeometry(5.4, 5.4);
			const mat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false });
			const plane = new THREE.Mesh(geo, mat);
			plane.rotation.x = -Math.PI / 2;
			plane.position.y = Y_BASE - 0.15;
			this.scene.add(plane);
			this._sceneDisposables.push(tex, geo, mat);
		},

		// ---------------------------------------------------------------
		// Render sob demanda
		// ---------------------------------------------------------------
		requestRender() {
			if (!this.renderer || this._renderScheduled) return;
			this._renderScheduled = true;
			this._frameId = requestAnimationFrame(() => {
				this._renderScheduled = false;
				this.sortLabels();
				this.renderer.render(this.scene, this.camera);
			});
		},

		scheduleResize() {
			if (this._resizePending) return;
			this._resizePending = true;
			requestAnimationFrame(() => {
				this._resizePending = false;
				this.applyResize();
			});
		},

		applyResize() {
			const canvas = this.$refs.scene;
			if (!canvas || !this.renderer) return;
			// Mede a caixa real do canvas (após o layout) para manter o aspecto correto.
			const w = canvas.clientWidth || (canvas.parentElement && canvas.parentElement.clientWidth) || 800;
			const h = canvas.clientHeight || 600;
			if (!w || !h) return;
			this.camera.aspect = w / h;
			this.camera.updateProjectionMatrix();
			this.renderer.setSize(w, h, false);
			this.requestRender();
		},

		// ---------------------------------------------------------------
		// Build / rebuild
		// ---------------------------------------------------------------
		scheduleRebuild() {
			if (!this.renderer || this._rebuildQueued) return;
			this._rebuildQueued = true;
			this.$nextTick(() => {
				this._rebuildQueued = false;
				this.rebuild();
			});
		},

		rebuild() {
			if (!this.renderer) return;
			try {
				this.disposeSilo();
				this.computeArcos();
				this.buildSilo();
				this.requestRender();
			} catch (e) {
				console.error(e);
			}
		},

		track(resource) {
			this._disposables.push(resource);
			return resource;
		},

		buildSilo() {
			const dark = this.theme === 'dark';
			this._disposables = [];
			this._labelGroups = [];
			this._dotGroups = [];
			this._pendingLabels = [];
			this._pendingCables = new Map();
			this._labelBatch = null;
			this.arcoGroups = {};
			this.caboCentralGroup = null;
			this.nivelGroup = null;
			this._anguloEscada = null;
			this.siloGroup = new THREE.Group();

			// Cabos, sensores e números: elementos 2D sem iluminação.
			this._cableMat = this.track(new THREE.LineBasicMaterial({ color: dark ? 0x1b1f25 : 0x23272e }));
			this._circleTex = this.track(this.makeCircleTexture());
			this._beadMat = this.track(new THREE.PointsMaterial({
				map: this._circleTex, size: 0.17, sizeAttenuation: true, vertexColors: true,
				transparent: true, alphaTest: 0.5, depthTest: false, depthWrite: false
			}));

			// Chapa galvanizada translúcida: um único material (mesma cor e mesma
			// transparência) para corpo, telhado, montantes, anéis e respiros.
			const shellColor = dark ? 0xc9d2db : 0x9aa6b2;
			this._shellMat = this.track(this.makeShellMaterial(shellColor, dark ? 0.3 : 0.32));
			this._roofMat = this._shellMat;
			this._ribMat = this._shellMat;
			this._metalMat = this._shellMat;
			// Só a camada da frente de cada objeto aparece (ver addShell / addFrontLayer).
			this._occluderMat = this.track(new THREE.MeshBasicMaterial({ colorWrite: false, side: THREE.BackSide }));
			// Transparente (sem cor) para entrar na fila dos transparentes, depois do grão.
			this._prepassMat = this.track(new THREE.MeshBasicMaterial({ colorWrite: false, transparent: true, depthWrite: true }));
			// Escada translúcida, porém bem mais opaca que a chapa.
			this._ladderMat = this.track(new THREE.MeshStandardMaterial({ color: 0x5b6570, metalness: 0.6, roughness: 0.45, transparent: true, opacity: 0.7, depthWrite: false }));
			this._floorMat = this.track(new THREE.MeshStandardMaterial({ color: dark ? 0x7d828a : 0x9aa0a8, metalness: 0.0, roughness: 0.95 }));
			this._lineMat = this.track(new THREE.LineBasicMaterial({
				color: dark ? 0xa9b4c0 : 0x56606b, transparent: true, opacity: dark ? 0.28 : 0.35, depthWrite: false
			}));

			if (this.silo.tipo === 'silo') {
				this.buildSiloTank();
			} else {
				this.buildWarehouse();
			}

			this.flushCables();
			this.flushLabels();
			this.scene.add(this.siloGroup);
			this._siloBox = new THREE.Box3().setFromObject(this.siloGroup);
		},

		disposeSilo() {
			if (this.siloGroup && this.scene) this.scene.remove(this.siloGroup);
			(this._disposables || []).forEach(d => { try { d.dispose && d.dispose(); } catch (_) { /* noop */ } });
			this._disposables = [];
			this._labelGroups = [];
			this._dotGroups = [];
			this._labelBatch = null;
			this.arcoGroups = {};
			this.caboCentralGroup = null;
			this.nivelGroup = null;
			this.siloGroup = null;
		},

		// Chapa translúcida com opacidade uniforme e quase sem sombreamento: sem
		// brilho/reflexo e com a cor vindo quase toda de um tom fixo (emissive), a
		// luz só varia ~20%. Assim corpo, montantes, anéis e respiros ficam com a
		// mesma aparência, independente de para onde cada face aponta.
		makeShellMaterial(color, opacity) {
			const c = new THREE.Color(color);
			return new THREE.MeshLambertMaterial({
				color: c.clone().multiplyScalar(0.3),
				emissive: c.clone().multiplyScalar(0.8),
				transparent: true, opacity, depthWrite: false
			});
		},

		// Casca (paredes/telhado): só a face externa voltada para a câmera é desenhada.
		// A face interna do lado oposto vira uma "tela" invisível que só grava
		// profundidade: esconde o outro lado do silo e tudo que está atrás dele
		// (escada, montantes...), mas não o que está dentro (grão, cabos, sensores).
		addShell(group, geo, front, orderFront = 6) {
			const oclusor = new THREE.Mesh(geo, this._occluderMat);
			oclusor.renderOrder = -1;
			const f = new THREE.Mesh(geo, front);
			f.renderOrder = orderFront;
			group.add(oclusor, f);
		},

		// Objeto translúcido mostrando só a camada mais próxima: uma pré-passada grava
		// a profundidade e a cor só passa onde é a superfície da frente.
		// Ordem: grão (2-3) -> pré-passada (5) -> chapa (6+). Assim a pré-passada não
		// esconde o produto/cabos atrás da peça, só a chapa e a própria parte de trás.
		addFrontLayer(mesh) {
			const pre = new THREE.Mesh(mesh.geometry, this._prepassMat);
			pre.renderOrder = 5;
			mesh.add(pre);
			return mesh;
		},

		// ---------------------------------------------------------------
		// Silo cilíndrico
		// ---------------------------------------------------------------
		buildSiloTank() {
			const g = this.siloGroup;

			// Fundação de concreto.
			const found = new THREE.Mesh(this.track(new THREE.CylinderGeometry(R + 0.3, R + 0.34, 0.14, 64)), this._floorMat);
			found.position.y = Y_BASE - 0.07;
			g.add(found);

			// Corpo com ondulação fina (chapa corrugada).
			const ondas = 36, amp = 0.012, aneis = ondas * 4;
			const profile = [];
			for (let i = 0; i <= aneis; i++) {
				const t = i / aneis;
				profile.push(new THREE.Vector2(R + amp * Math.sin(t * Math.PI * 2 * ondas), Y_BASE + t * CORPO_H));
			}
			this.addShell(g, this.track(new THREE.LatheGeometry(profile, 72)), this._shellMat);

			// Emendas horizontais das chapas.
			const seams = this.makeRings(R + amp + 0.004, Array.from({ length: 7 }, (_, i) => Y_BASE + (CORPO_H * (i + 1)) / 8), 72);
			seams.renderOrder = 7;
			g.add(seams);

			// Montantes verticais externos.
			const nMont = 16;
			const montGeo = new THREE.BoxGeometry(0.045, CORPO_H, 0.024);
			const montPartes = [];
			for (let i = 0; i < nMont; i++) {
				const a = (i / nMont) * Math.PI * 2 + Math.PI / nMont;
				montPartes.push([montGeo, { x: (R + 0.024) * Math.cos(a), y: Y_BASE + CORPO_H / 2, z: (R + 0.024) * Math.sin(a), ry: Math.PI / 2 - a }]);
			}
			const mont = new THREE.Mesh(this.mergeParts(montPartes), this._ribMat);
			mont.renderOrder = 7;
			this.addFrontLayer(mont);
			g.add(mont);

			// Cantoneira de base e anel do beiral.
			const baseRing = new THREE.Mesh(this.track(new THREE.TorusGeometry(R + 0.02, 0.03, 6, 72)), this._metalMat);
			baseRing.rotation.x = Math.PI / 2; baseRing.position.y = Y_BASE + 0.02;
			baseRing.renderOrder = 7;
			this.addFrontLayer(baseRing);
			g.add(baseRing);
			const eaveRing = new THREE.Mesh(this.track(new THREE.TorusGeometry(R_TELHADO, 0.018, 6, 72)), this._ribMat);
			eaveRing.rotation.x = Math.PI / 2; eaveRing.position.y = Y_BEIRAL;
			eaveRing.renderOrder = 7;
			this.addFrontLayer(eaveRing);
			g.add(eaveRing);

			this.buildRoof(g);
			this.buildLadder(g);

			// Grão.
			this.buildGrain();

			// Cabos + sensores por arco.
			const nArcos = this.arcosExistentes.length;
			this.arcosExistentes.forEach((arco, index) => {
				const cabos = this.silo.cabos
					.filter(c => c.arco === arco)
					.sort((a, b) => (a.posicao ?? 0) - (b.posicao ?? 0));
				const caboPorArco = cabos.length;
				const raioArco = (RAIO_ARCO_MAP[nArcos] && RAIO_ARCO_MAP[nArcos][index]) ?? R * 0.5;

				const arcoGroup = this.newSensorGroup(`arco-${arco}`);
				const beadPos = [];
				const beadCol = [];

				for (let k = 0; k < caboPorArco; k++) {
					const cabo = cabos[k];
					const dados = (this.termometria?.dados || []).filter(d => d.cabo === cabo.cabo);
					if (dados.length !== 1) continue;
					const dado = dados[0];

					const angle = this.anguloNoArco(k, caboPorArco, arco);
					const x = raioArco * Math.cos(angle);
					const z = raioArco * Math.sin(angle);
					const topY = this.roofYAt(raioArco) - 0.02;
					const botY = Y_BASE;
					this.addCable(arcoGroup, x, z, topY, botY);

					const sensores = Math.max(1, cabo.sensores || 0);
					const comprimento = 2.47 - raioArco * 0.5 + EXTRA_TOPO_CABO;
					const y0 = botY + MARGEM_CABO;
					const y1 = Math.max(y0, Math.min(botY + comprimento - MARGEM_CABO, this.roofYAt(raioArco) - FOLGA_TELHADO));
					for (let j = 0; j < sensores; j++) {
						const y = sensores > 1 ? y0 + ((y1 - y0) / (sensores - 1)) * j : (y0 + y1) / 2;
						const temp = dado.temperaturas ? dado.temperaturas[`s${j + 1}`] : null;
						beadPos.push(new THREE.Vector3(x, y, z));
						beadCol.push(new THREE.Color(this.corPorTemp(temp)));
						this.addSensorPill(arcoGroup.userData.labels, temp, new THREE.Vector3(x, y, z));
					}
					this.addLabel(arcoGroup, String(dado.cabo), new THREE.Vector3(x, topY + 0.05, z), { size: 0.15, color: COR_CABO });
				}

				const beads = this.makeBeads(beadPos, beadCol);
				if (beads) arcoGroup.userData.dots.add(beads);
				arcoGroup.visible = this.arcosVisiveis[arco] !== false;
				this.arcoGroups[arco] = arcoGroup;
				g.add(arcoGroup);
			});

			this.buildCaboCentral();
		},

		buildRoof(g) {
			// Cone com nervuras radiais em relevo.
			const nNerv = 32;
			const geo = this.track(new THREE.ConeGeometry(R_TELHADO, TELHADO_H, nNerv * 4, 4, true));
			const pos = geo.attributes.position;
			for (let i = 0; i < pos.count; i++) {
				const x = pos.getX(i), z = pos.getZ(i);
				const rho = Math.hypot(x, z);
				const nerv = Math.pow(0.5 + 0.5 * Math.cos(Math.atan2(z, x) * nNerv), 6);
				pos.setY(i, pos.getY(i) + 0.028 * nerv * (rho / R_TELHADO));
			}
			geo.translate(0, Y_BEIRAL + TELHADO_H / 2, 0);
			geo.computeVertexNormals();
			this.addShell(g, geo, this._roofMat, 8);

			// Linhas das nervuras (reforçam a leitura do telhado).
			const apex = new THREE.Vector3(0, Y_BEIRAL + TELHADO_H + 0.02, 0);
			const ribPts = [];
			for (let i = 0; i < nNerv; i++) {
				const a = (i / nNerv) * Math.PI * 2;
				ribPts.push(apex.clone(), new THREE.Vector3(R_TELHADO * Math.cos(a), Y_BEIRAL + 0.028, R_TELHADO * Math.sin(a)));
			}
			g.add(new THREE.LineSegments(this.track(new THREE.BufferGeometry().setFromPoints(ribPts)), this._lineMat));

			// Boca de carga e respiros: mesma chapa translúcida do silo (uma malha só).
			const topY = Y_BEIRAL + TELHADO_H;
			const pecas = [
				[new THREE.CylinderGeometry(0.2, 0.24, 0.1, 24), { y: topY - 0.02 }],
				[new THREE.ConeGeometry(0.27, 0.12, 24), { y: topY + 0.1 }]
			];
			const ventBody = new THREE.CylinderGeometry(0.06, 0.07, 0.12, 12);
			const ventCap = new THREE.ConeGeometry(0.11, 0.07, 12);
			[0.25, 0.75, 1.25, 1.75].forEach(k => {
				const a = Math.PI * k;
				const r = R * 0.72;
				const y = this.roofYAt(r);
				pecas.push([ventBody, { x: r * Math.cos(a), y: y + 0.04, z: r * Math.sin(a) }]);
				pecas.push([ventCap, { x: r * Math.cos(a), y: y + 0.13, z: r * Math.sin(a) }]);
			});
			const acessorios = new THREE.Mesh(this.mergeParts(pecas), this._roofMat);
			acessorios.renderOrder = 9;
			this.addFrontLayer(acessorios);
			g.add(acessorios);
		},

		buildLadder(group) {
			// Eixos locais: x = largura da escada, z = para fora do silo, y = altura.
			// Todas as peças usam o mesmo material e viram uma única malha.
			const partes = [];
			const meia = 0.045;
			const railGeo = new THREE.CylinderGeometry(0.006, 0.006, CORPO_H, 6);
			const rungGeo = new THREE.CylinderGeometry(0.004, 0.004, meia * 2, 5);
			partes.push([railGeo, { x: -meia, y: Y_BASE + CORPO_H / 2 }]);
			partes.push([railGeo, { x: meia, y: Y_BASE + CORPO_H / 2 }]);
			// Escada termina na altura do beiral (topo do corpo).
			for (let i = 0; i < 20; i++) {
				partes.push([rungGeo, { y: Y_BASE + 0.05 + i * 0.1, rz: Math.PI / 2 }]);
			}

			// Gaiola: aros em "U" presos nos montantes da escada, abertos para o silo
			// e envolvendo o lado de fora, ligados por barras verticais.
			const rc = 0.07;          // raio do aro
			const zc = 0.06;          // centro do aro (para fora do silo)
			const pontoAro = (t) => new THREE.Vector3(rc * Math.cos(t), 0, zc + rc * Math.sin(t));
			const uPts = [new THREE.Vector3(-meia, 0, 0), new THREE.Vector3(-rc, 0, zc * 0.5)];
			for (let k = 0; k <= 12; k++) uPts.push(pontoAro(Math.PI - (k / 12) * Math.PI));
			uPts.push(new THREE.Vector3(rc, 0, zc * 0.5), new THREE.Vector3(meia, 0, 0));
			const hoopGeo = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(uPts), 32, 0.0035, 4, false);
			const yIni = -0.3, yFim = Y_BEIRAL, nAros = 13;
			for (let i = 0; i < nAros; i++) {
				partes.push([hoopGeo, { y: yIni + ((yFim - yIni) * i) / (nAros - 1) }]);
			}
			const barGeo = new THREE.CylinderGeometry(0.003, 0.003, yFim - yIni, 4);
			[Math.PI, Math.PI * 0.75, Math.PI * 0.5, Math.PI * 0.25, 0].forEach(t => {
				const p = pontoAro(t);
				partes.push([barGeo, { x: p.x, y: (yIni + yFim) / 2, z: p.z }]);
			});

			const ladder = new THREE.Mesh(this.mergeParts(partes), this._ladderMat);
			ladder.renderOrder = 7; // depois da chapa: fica por fora do silo
			this.addFrontLayer(ladder);
			const a1 = this.anguloDoCabo(1) + 0.16; // levemente à esquerda do cabo 1
			ladder.position.set((R + 0.045) * Math.cos(a1), 0, (R + 0.045) * Math.sin(a1));
			ladder.rotation.y = Math.PI / 2 - a1;
			group.add(ladder);
			this._anguloEscada = a1;
		},

		// ---------------------------------------------------------------
		// Grão: superfície com relevo, volume ajustado ao nível (%).
		// ---------------------------------------------------------------
		buildGrain() {
			const pct = this.nivelPercentual();
			const grp = new THREE.Group();
			grp.visible = this.nivelVisivel;
			this.nivelGroup = grp;
			this.siloGroup.add(grp);
			if (pct <= 0.5) return;

			const rr = R - 0.012;
			const surface = this.makeGrainSurfaceFn(pct, rr);

			// Volume fechado desenhado só pelas faces externas: sem artefatos de
			// ordenação de transparência ao girar a câmera.
			const tex = this.track(this.makeGrainTexture());
			const mat = this.track(new THREE.MeshLambertMaterial({
				map: tex, vertexColors: true, transparent: true, opacity: 0.82,
				side: THREE.FrontSide, depthWrite: true
			}));

			const cFundo = new THREE.Color(GRAO_FUNDO);
			const cMeio = new THREE.Color(GRAO_MEIO);
			const cTopo = new THREE.Color(GRAO_TOPO);
			const corAltura = (t) => (t < 0.5 ? cFundo.clone().lerp(cMeio, t * 2) : cMeio.clone().lerp(cTopo, (t - 0.5) * 2));
			const yMaxSurf = Y_BEIRAL + TELHADO_H;

			// Superfície superior (malha polar).
			const nr = 24, na = 96;
			const pos = [], uv = [], col = [], idx = [];
			for (let i = 0; i <= nr; i++) {
				const r = (i / nr) * rr;
				for (let j = 0; j <= na; j++) {
					const a = (j / na) * Math.PI * 2;
					const x = r * Math.cos(a), z = r * Math.sin(a);
					const y = surface(r, a);
					pos.push(x, y, z);
					uv.push(x * 1.6, z * 1.6);
					const c = corAltura(THREE.MathUtils.clamp(0.55 + ((y - Y_BASE) / (yMaxSurf - Y_BASE)) * 0.45, 0, 1));
					col.push(c.r, c.g, c.b);
				}
			}
			for (let i = 0; i < nr; i++) {
				for (let j = 0; j < na; j++) {
					const a = i * (na + 1) + j, b = a + na + 1;
					idx.push(a, a + 1, b, a + 1, b + 1, b);
				}
			}
			const topGeo = this.track(new THREE.BufferGeometry());
			topGeo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
			topGeo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
			topGeo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
			topGeo.setIndex(idx);
			topGeo.computeVertexNormals();
			// Vértices do centro são degenerados: normal para cima.
			const nor = topGeo.attributes.normal;
			for (let j = 0; j <= na; j++) nor.setXYZ(j, 0, 1, 0);
			const top = new THREE.Mesh(topGeo, mat);
			top.renderOrder = 3;
			grp.add(top);

			// Parede lateral do grão (encostada na chapa).
			const rows = 6;
			const wPos = [], wUv = [], wCol = [], wIdx = [], wNor = [];
			for (let k = 0; k <= rows; k++) {
				const f = k / rows;
				for (let j = 0; j <= na; j++) {
					const a = (j / na) * Math.PI * 2;
					const y = Y_BASE + (surface(rr, a) - Y_BASE) * f;
					wPos.push(rr * Math.cos(a), y, rr * Math.sin(a));
					wNor.push(Math.cos(a), 0, Math.sin(a));
					wUv.push(a * rr * 1.6, y * 1.6);
					const c = corAltura(THREE.MathUtils.clamp((y - Y_BASE) / (yMaxSurf - Y_BASE), 0, 1));
					wCol.push(c.r, c.g, c.b);
				}
			}
			for (let k = 0; k < rows; k++) {
				for (let j = 0; j < na; j++) {
					const a = k * (na + 1) + j, b = a + na + 1;
					wIdx.push(a, b, a + 1, a + 1, b, b + 1);
				}
			}
			const wallGeo = this.track(new THREE.BufferGeometry());
			wallGeo.setAttribute('position', new THREE.Float32BufferAttribute(wPos, 3));
			wallGeo.setAttribute('normal', new THREE.Float32BufferAttribute(wNor, 3));
			wallGeo.setAttribute('uv', new THREE.Float32BufferAttribute(wUv, 2));
			wallGeo.setAttribute('color', new THREE.Float32BufferAttribute(wCol, 3));
			wallGeo.setIndex(wIdx);
			const wall = new THREE.Mesh(wallGeo, mat);
			wall.renderOrder = 2;
			grp.add(wall);
		},

		// Retorna f(r, ângulo) => altura da superfície do grão. O cone de acomodação
		// + ondulações são deslocados verticalmente (busca binária) até que a altura
		// média sobre a área do silo corresponda ao nível percentual.
		makeGrainSurfaceFn(pct, rr) {
			const rand = rng((this.silo.id || 1) * 9301 + 49297);
			// Topo do produto: cone centrado no eixo do silo (enchimento pelo centro),
			// com inclinação do ângulo de repouso, espalhando por igual até as laterais.
			const inclinacao = Math.tan(THREE.MathUtils.degToRad(ANGULO_REPOUSO));
			// Ondulação bem sutil, só para a superfície não parecer lisa demais.
			const ondas = Array.from({ length: 3 }, (_, k) => ({
				amp: (0.008 / (k + 1)) * (0.6 + rand() * 0.8),
				fa: 2 + k + Math.floor(rand() * 2),
				fr: 1 + rand() * 2 * (k + 1),
				pa: rand() * Math.PI * 2,
				pr: rand() * Math.PI * 2
			}));
			const alturaRel = (r, a) => {
				let h = inclinacao * (rr - r);
				// Ondulação zerada no centro para manter o bico do cone.
				ondas.forEach(o => { h += o.amp * (r / rr) * Math.sin(a * o.fa + o.pa) * Math.sin((r / rr) * o.fr * Math.PI + o.pr); });
				return h;
			};
			const teto = (r) => this.roofYAt(r) - 0.06;
			const calc = (base, r, a) => Math.min(teto(r), Math.max(Y_BASE + 0.004, Y_BASE + base + alturaRel(r, a)));

			// Amostragem ponderada por área para achar a base que reproduz o volume.
			const amostras = [];
			const nr = 14, na = 24;
			for (let i = 0; i < nr; i++) {
				const r = ((i + 0.5) / nr) * rr;
				for (let j = 0; j < na; j++) amostras.push({ r, a: (j / na) * Math.PI * 2, w: r });
			}
			const pesoTotal = amostras.reduce((s, p) => s + p.w, 0);
			const media = (base) => amostras.reduce((s, p) => s + (calc(base, p.r, p.a) - Y_BASE) * p.w, 0) / pesoTotal;
			// 100%: bico do cone logo abaixo do telhado, cobrindo o último sensor de todos os cabos.
			const baseCheio = teto(0) - 0.02 - Y_BASE - inclinacao * rr;
			const alvo = (pct / 100) * media(baseCheio);
			let lo = -inclinacao * rr - 0.3, hi = baseCheio;
			for (let it = 0; it < 30; it++) {
				const mid = (lo + hi) / 2;
				if (media(mid) < alvo) lo = mid; else hi = mid;
			}
			const base = (lo + hi) / 2;
			return (r, a) => calc(base, r, a);
		},

		makeGrainTexture() {
			const s = 256;
			const cv = document.createElement('canvas');
			cv.width = cv.height = s;
			const ctx = cv.getContext('2d');
			ctx.fillStyle = '#e8e2d6';
			ctx.fillRect(0, 0, s, s);
			const rand = rng(7);
			for (let i = 0; i < 5200; i++) {
				const x = rand() * s, y = rand() * s;
				const l = Math.floor(170 + rand() * 85);
				ctx.fillStyle = `rgb(${l},${Math.floor(l * 0.95)},${Math.floor(l * 0.85)})`;
				ctx.beginPath();
				ctx.ellipse(x, y, 1.2 + rand() * 1.4, 0.8 + rand() * 0.9, rand() * Math.PI, 0, Math.PI * 2);
				ctx.fill();
			}
			const tex = new THREE.CanvasTexture(cv);
			tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
			tex.colorSpace = THREE.SRGBColorSpace;
			return tex;
		},

		buildCaboCentral() {
			const dados = this.termometria?.dados || [];
			const possui = this.silo.cabos.some(c => c.arco === 999);
			if (!possui || dados.length === 0) {
				this.caboCentralGroup = null;
				return;
			}
			const last = dados[dados.length - 1];
			const sensores = Math.max(1, last.sensores || 0);

			const grp = this.newSensorGroup('cabo-central');
			const topY = this.roofYAt(0) - 0.04;
			const botY = Y_BASE;
			this.addCable(grp, 0, 0, topY, botY);

			const beadPos = [];
			const beadCol = [];
			const y0 = botY + MARGEM_CABO;
			const y1 = Math.max(y0, Math.min(botY + 2.5 + EXTRA_TOPO_CABO - MARGEM_CABO, this.roofYAt(0) - FOLGA_TELHADO));
			for (let i = 0; i < sensores; i++) {
				const y = sensores > 1 ? y0 + ((y1 - y0) / (sensores - 1)) * i : (y0 + y1) / 2;
				const temp = last.temperaturas ? last.temperaturas[`s${i + 1}`] : null;
				beadPos.push(new THREE.Vector3(0, y, 0));
				beadCol.push(new THREE.Color(this.corPorTemp(temp)));
				this.addSensorPill(grp.userData.labels, temp, new THREE.Vector3(0, y, 0));
			}
			const beads = this.makeBeads(beadPos, beadCol);
			if (beads) grp.userData.dots.add(beads);

			// Número do cabo central = maior número de cabo + 1.
			const centralNumero = Math.max(0, ...this.silo.cabos.filter(c => c.arco !== 999).map(c => c.cabo)) + 1;
			this.addLabel(grp, String(centralNumero), new THREE.Vector3(0, topY + 0.05, 0), { size: 0.15, color: COR_CABO });

			grp.visible = this.caboCentralVisivel;
			this.caboCentralGroup = grp;
			this.siloGroup.add(grp);
		},

		// ---------------------------------------------------------------
		// Armazém
		// ---------------------------------------------------------------
		buildWarehouse() {
			const g = this.siloGroup;
			const nArcos = this.arcosExistentes.length;
			const escala = 1.7;
			const largura = 3.5 * escala;
			const altura = 0.9 * escala;
			const profundidade = Math.max(1, nArcos * 0.5) * escala;
			const yFloor = -1;
			const hL = largura / 2;
			const hP = profundidade / 2;

			// Piso.
			const floor = new THREE.Mesh(this.track(new THREE.BoxGeometry(largura + 0.3, 0.08, profundidade + 0.3)), this._floorMat);
			floor.position.y = yFloor - 0.04;
			g.add(floor);

			// Corpo.
			const boxGeo = this.track(new THREE.BoxGeometry(largura, altura, profundidade));
			boxGeo.translate(0, yFloor + altura / 2, 0);
			g.add(new THREE.LineSegments(this.track(new THREE.EdgesGeometry(boxGeo)), this._lineMat));
			// Sem a face de cima (fica sob o telhado e seria uma camada extra).
			const idxBox = Array.from(boxGeo.index.array);
			idxBox.splice(12, 6);
			boxGeo.setIndex(idxBox);
			boxGeo.clearGroups();
			this.addShell(g, boxGeo, this._shellMat);

			// Telhado de duas águas (+ oitões).
			const anguloBase = Math.PI / 6;
			const telhadoAltura = hL * Math.tan(anguloBase);
			const yBase = yFloor + altura;
			const v1 = [-hL, yBase, -hP], v2 = [hL, yBase, -hP], v3 = [hL, yBase, hP], v4 = [-hL, yBase, hP];
			const v5 = [0, yBase + telhadoAltura, -hP], v6 = [0, yBase + telhadoAltura, hP];

			// Faces orientadas para fora (necessário para desenhar só a camada da frente).
			const verts = [].concat(v1, v4, v6, v1, v6, v5, v2, v5, v6, v2, v6, v3, v1, v5, v2, v4, v3, v6);
			const telhadoGeometry = this.track(new THREE.BufferGeometry());
			telhadoGeometry.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
			telhadoGeometry.computeVertexNormals();
			this.addShell(g, telhadoGeometry, this._shellMat, 8);

			const roofLines = [].concat(
				[0, yBase + telhadoAltura, -hP], [0, yBase + telhadoAltura, hP],
				[-hL, yBase, -hP], [-hL, yBase, hP],
				[hL, yBase, -hP], [hL, yBase, hP],
				[-hL, yBase, -hP], [0, yBase + telhadoAltura, -hP],
				[hL, yBase, -hP], [0, yBase + telhadoAltura, -hP],
				[-hL, yBase, hP], [0, yBase + telhadoAltura, hP],
				[hL, yBase, hP], [0, yBase + telhadoAltura, hP]
			);
			const roofLineGeo = this.track(new THREE.BufferGeometry());
			roofLineGeo.setAttribute('position', new THREE.Float32BufferAttribute(roofLines, 3));
			g.add(new THREE.LineSegments(roofLineGeo, this._lineMat));

			// Cabos em fileiras.
			const fileiraEspaco = profundidade / (nArcos + 1);
			const maxSens = Math.max(1, ...this.silo.cabos.map(c => c.sensores || 1));
			const numPos = 9;
			const posX = Array.from({ length: numPos }, (_, i) => -hL + ((i + 1) * largura) / (numPos + 1));

			this.arcosExistentes.forEach((arco, idx) => {
				const z = -hP + fileiraEspaco * (idx + 1);
				const cabos = this.silo.cabos.filter(c => c.arco === arco);
				const arcoGroup = this.newSensorGroup(`arco-${arco}`);
				const beadPos = [];
				const beadCol = [];

				cabos.forEach(cabo => {
					const dados = (this.termometria?.dados || []).filter(d => d.cabo === cabo.cabo);
					if (dados.length !== 1) return;
					const dado = dados[0];
					const pos = parseInt(cabo.posicao);
					if (Number.isNaN(pos) || pos < 1 || pos > 9) return;

					const x = posX[pos - 1];
					const roofUnderY = yBase + telhadoAltura * (1 - Math.abs(x) / hL) - 0.05;
					const botY = yFloor + 0.02;
					this.addCable(arcoGroup, x, z, roofUnderY, botY);

					const sensores = Math.max(1, cabo.sensores || 0);
					const comprimento = (roofUnderY - botY) * (sensores / maxSens);
					const y0 = botY + MARGEM_CABO;
					const y1 = Math.max(y0, botY + comprimento - MARGEM_CABO);
					for (let j = 0; j < sensores; j++) {
						const y = sensores > 1 ? y0 + ((y1 - y0) / (sensores - 1)) * j : (y0 + y1) / 2;
						const temp = dado.temperaturas ? dado.temperaturas[`s${j + 1}`] : null;
						beadPos.push(new THREE.Vector3(x, y, z));
						beadCol.push(new THREE.Color(this.corPorTemp(temp)));
						this.addSensorPill(arcoGroup.userData.labels, temp, new THREE.Vector3(x, y, z));
					}
					this.addLabel(arcoGroup, String(dado.cabo), new THREE.Vector3(x, roofUnderY + 0.08, z), { size: 0.13, color: COR_CABO });
				});

				const beads = this.makeBeads(beadPos, beadCol);
				if (beads) arcoGroup.userData.dots.add(beads);
				arcoGroup.visible = this.arcosVisiveis[arco] !== false;
				this.arcoGroups[arco] = arcoGroup;
				g.add(arcoGroup);
			});
		},

		// ---------------------------------------------------------------
		// Helpers
		// ---------------------------------------------------------------
		// Grupo de um arco/cabo, com subgrupo para os valores de temperatura.
		newSensorGroup(name) {
			const grp = new THREE.Group();
			grp.name = name;
			const labels = new THREE.Group();
			labels.visible = this.valoresVisiveis;
			grp.add(labels);
			grp.userData.labels = labels;
			this._labelGroups.push(labels);
			// Com os valores ocultos, os sensores viram pontos coloridos.
			const dots = new THREE.Group();
			dots.visible = !this.valoresVisiveis;
			grp.add(dots);
			grp.userData.dots = dots;
			this._dotGroups.push(dots);
			return grp;
		},

		roofYAt(raio) {
			return Y_BEIRAL + TELHADO_H * (1 - Math.min(raio, R_TELHADO) / R_TELHADO);
		},

		anguloDoCabo(numero) {
			const arcoCabos = (this.silo.cabos || []).filter(c => c.arco !== 999);
			if (!arcoCabos.length) return 0;
			let alvo = arcoCabos.find(c => c.cabo === numero);
			if (!alvo) alvo = arcoCabos.reduce((a, b) => (a.cabo <= b.cabo ? a : b));
			const list = arcoCabos.filter(c => c.arco === alvo.arco).sort((a, b) => (a.posicao ?? 0) - (b.posicao ?? 0));
			return this.anguloNoArco(list.findIndex(c => c.cabo === alvo.cabo), list.length, alvo.arco);
		},

		// Ângulo do k-ésimo cabo (por posição) de um arco com n cabos. O primeiro cabo
		// do arco 1 fica um pouco à direita da escada; cada arco seguinte começa no
		// espaço livre entre os cabos do arco anterior (meio passo adiante), e os
		// cabos seguem sempre no mesmo sentido.
		anguloNoArco(k, n, arco) {
			const cabos = (this.silo.cabos || []).filter(c => c.arco !== 999);
			if (!cabos.length || !n) return 0;
			const arcos = [...new Set(cabos.map(c => c.arco))].sort((a, b) => a - b);
			const qtd = (a) => cabos.filter(c => c.arco === a).length;
			let inicio = ((qtd(arcos[0]) - 1) / qtd(arcos[0])) * Math.PI * 2;
			const idx = Math.max(0, arcos.indexOf(arco));
			for (let i = 1; i <= idx; i++) {
				inicio -= Math.PI / qtd(arcos[i - 1]);
			}
			return inicio - (k / n) * Math.PI * 2;
		},

		// Vários anéis horizontais numa única malha de linhas.
		makeRings(raio, alturas, seg) {
			const pts = [];
			alturas.forEach(y => {
				for (let i = 0; i < seg; i++) {
					const a0 = (i / seg) * Math.PI * 2, a1 = ((i + 1) / seg) * Math.PI * 2;
					pts.push(
						new THREE.Vector3(raio * Math.cos(a0), y, raio * Math.sin(a0)),
						new THREE.Vector3(raio * Math.cos(a1), y, raio * Math.sin(a1))
					);
				}
			});
			return new THREE.LineSegments(this.track(new THREE.BufferGeometry().setFromPoints(pts)), this._lineMat);
		},

		// Junta peças [geometria, {x, y, z, rx, ry, rz}] numa só geometria (1 draw call).
		mergeParts(partes) {
			const dummy = new THREE.Object3D();
			const originais = new Set();
			const clones = partes.map(([geo, t]) => {
				originais.add(geo);
				dummy.position.set(t.x || 0, t.y || 0, t.z || 0);
				dummy.rotation.set(t.rx || 0, t.ry || 0, t.rz || 0);
				dummy.updateMatrix();
				return geo.clone().applyMatrix4(dummy.matrix);
			});
			const merged = mergeGeometries(clones, false);
			clones.forEach(c => c.dispose());
			originais.forEach(g => g.dispose());
			return this.track(merged);
		},

		// Cabo = linha 2D sem iluminação (agrupadas por arco em flushCables).
		addCable(group, x, z, topY, botY) {
			if (!this._pendingCables.has(group)) this._pendingCables.set(group, []);
			this._pendingCables.get(group).push(new THREE.Vector3(x, botY, z), new THREE.Vector3(x, topY, z));
		},

		flushCables() {
			this._pendingCables.forEach((pts, group) => {
				group.add(new THREE.LineSegments(this.track(new THREE.BufferGeometry().setFromPoints(pts)), this._cableMat));
			});
			this._pendingCables = new Map();
		},

		// Textura de círculo para o disco do sensor.
		makeCircleTexture() {
			const s = 64;
			const cv = document.createElement('canvas');
			cv.width = cv.height = s;
			const ctx = cv.getContext('2d');
			ctx.beginPath();
			ctx.arc(s / 2, s / 2, s / 2 - 4, 0, Math.PI * 2);
			ctx.fillStyle = '#fff';
			ctx.fill();
			ctx.lineWidth = 4;
			ctx.strokeStyle = 'rgba(20,25,32,0.55)';
			ctx.stroke();
			const tex = new THREE.CanvasTexture(cv);
			tex.minFilter = THREE.LinearFilter;
			tex.generateMipmaps = false;
			return tex;
		},

		// Sensores = discos 2D (billboards) sem iluminação, por cima de tudo.
		makeBeads(positions, colors) {
			if (!positions.length) return null;
			const pos = new Float32Array(positions.length * 3);
			const col = new Float32Array(positions.length * 3);
			for (let i = 0; i < positions.length; i++) {
				pos[i * 3] = positions[i].x; pos[i * 3 + 1] = positions[i].y; pos[i * 3 + 2] = positions[i].z;
				col[i * 3] = colors[i].r; col[i * 3 + 1] = colors[i].g; col[i * 3 + 2] = colors[i].b;
			}
			const geo = this.track(new THREE.BufferGeometry());
			geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
			geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
			const pts = new THREE.Points(geo, this._beadMat);
			pts.frustumCulled = false;
			pts.renderOrder = 15;
			return pts;
		},

		// Rótulos sempre por cima: texto com contorno ou, com `bg`, pílula colorida
		// centralizada no ponto. São acumulados aqui e desenhados todos juntos em
		// flushLabels: um atlas de textura + uma malha.
		addLabel(owner, text, position, opts = {}) {
			this._pendingLabels.push({
				owner,
				text,
				color: opts.color ?? '#ffffff',
				bg: opts.bg ?? null,
				size: opts.size ?? 0.12,
				anchor: position.clone()
			});
		},

		// Sensor = pílula horizontal na cor da faixa com o valor dentro (como na tabela).
		// Todas as pílulas têm o mesmo tamanho (ver largura fixa em flushLabels).
		addSensorPill(owner, temp, position) {
			this.addLabel(owner, this.tempLabel(temp), position, { size: 0.085, bg: this.corPorTemp(temp), color: '#0f172a' });
		},

		flushLabels() {
			const itens = this._pendingLabels;
			this._pendingLabels = [];
			if (!itens.length) return;

			// Atlas com cada texto/cor única (mesmo desenho dos antigos sprites).
			const weight = '700';
			const stroke = 'rgba(15,20,28,0.9)';
			const limite = Math.min(4096, this.renderer.capabilities.maxTextureSize || 4096);
			const larguraAtlas = Math.min(2048, limite);
			const chave = (it) => `${it.text}|${it.color}|${it.bg || ''}`;
			const unicos = [...new Map(itens.map(it => [chave(it), it])).values()];
			const measure = document.createElement('canvas').getContext('2d');

			let escala = 1, celulas, alturaAtlas;
			for (;;) {
				const fontPx = Math.round(72 * escala);
				const pad = Math.round(16 * escala);
				measure.font = `${weight} ${fontPx}px Arial, sans-serif`;
				// Pílulas com largura única: a do maior valor.
				const larguraPilula = Math.max(0, ...unicos.filter(it => it.bg).map(it => Math.ceil(measure.measureText(it.text).width))) + pad * 3;
				celulas = new Map();
				let x = 0, y = 0;
				const ch = fontPx + pad * 2;
				unicos.forEach(it => {
					const cw = it.bg ? larguraPilula : Math.max(1, Math.ceil(measure.measureText(it.text).width)) + pad * 2;
					if (x + cw > larguraAtlas) { x = 0; y += ch; }
					celulas.set(chave(it), { x, y, cw, ch, fontPx });
					x += cw;
				});
				alturaAtlas = THREE.MathUtils.ceilPowerOfTwo(y + ch);
				if (alturaAtlas <= limite || escala < 0.2) break;
				escala *= 0.8;
			}

			const canvas = document.createElement('canvas');
			canvas.width = larguraAtlas;
			canvas.height = alturaAtlas;
			const ctx = canvas.getContext('2d');
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			ctx.lineJoin = 'round';
			unicos.forEach(it => {
				const c = celulas.get(chave(it));
				const cx = c.x + c.cw / 2, cy = c.y + c.ch / 2;
				ctx.font = `${weight} ${c.fontPx}px Arial, sans-serif`;
				if (it.bg) {
					const m = 3 * escala;
					ctx.beginPath();
					this.roundRectPath(ctx, c.x + m, c.y + m, c.cw - m * 2, c.ch - m * 2, (c.ch - m * 2) * 0.3);
					ctx.fillStyle = it.bg;
					ctx.fill();
					ctx.lineWidth = 3 * escala;
					ctx.strokeStyle = 'rgba(15,23,42,0.55)';
					ctx.stroke();
				} else {
					ctx.lineWidth = 10 * escala;
					ctx.strokeStyle = stroke;
					ctx.strokeText(it.text, cx, cy);
				}
				ctx.fillStyle = it.color;
				ctx.fillText(it.text, cx, cy + 2 * escala);
			});
			const tex = this.track(new THREE.CanvasTexture(canvas));
			tex.minFilter = THREE.LinearFilter;
			tex.magFilter = THREE.LinearFilter;
			tex.generateMipmaps = false;

			// Quads "billboard": o deslocamento é aplicado no espaço da câmera,
			// igual a um THREE.Sprite (center (0.5, 0), ou (0.5, 0.5) nas pílulas).
			const n = itens.length;
			const pos = new Float32Array(n * 12);
			const off = new Float32Array(n * 8);
			const uv = new Float32Array(n * 8);
			itens.forEach((it, i) => {
				const c = celulas.get(chave(it));
				const w = it.size * (c.cw / c.ch), h = it.size;
				const y0 = it.bg ? -h / 2 : 0; // pílulas centradas no sensor
				const u0 = c.x / larguraAtlas, u1 = (c.x + c.cw) / larguraAtlas;
				const vTop = 1 - c.y / alturaAtlas, vBot = 1 - (c.y + c.ch) / alturaAtlas;
				for (let k = 0; k < 4; k++) {
					pos[i * 12 + k * 3] = it.anchor.x;
					pos[i * 12 + k * 3 + 1] = it.anchor.y;
					pos[i * 12 + k * 3 + 2] = it.anchor.z;
				}
				off.set([-w / 2, y0, w / 2, y0, w / 2, y0 + h, -w / 2, y0 + h], i * 8);
				uv.set([u0, vBot, u1, vBot, u1, vTop, u0, vTop], i * 8);
			});
			const geo = this.track(new THREE.BufferGeometry());
			geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
			geo.setAttribute('offset', new THREE.BufferAttribute(off, 2));
			geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
			const index = new THREE.BufferAttribute(n * 4 > 65535 ? new Uint32Array(n * 6) : new Uint16Array(n * 6), 1);
			index.setUsage(THREE.DynamicDrawUsage);
			geo.setIndex(index);

			const mat = this.track(new THREE.ShaderMaterial({
				uniforms: { map: { value: tex } },
				vertexShader: `
					attribute vec2 offset;
					varying vec2 vUv;
					void main() {
						vUv = uv;
						vec4 mv = modelViewMatrix * vec4( position, 1.0 );
						mv.xy += offset;
						gl_Position = projectionMatrix * mv;
					}`,
				fragmentShader: `
					uniform sampler2D map;
					varying vec2 vUv;
					void main() {
						vec4 c = texture2D( map, vUv );
						if ( c.a < 0.01 ) discard;
						gl_FragColor = c;
						#include <colorspace_fragment>
					}`,
				transparent: true, depthTest: false, depthWrite: false
			}));
			const mesh = new THREE.Mesh(geo, mat);
			mesh.frustumCulled = false;
			mesh.renderOrder = 20;
			this.siloGroup.add(mesh);
			this._labelBatch = { mesh, itens, ordem: new Array(n), dist: new Float32Array(n) };
		},

		// A cada quadro: só os rótulos visíveis, do mais distante para o mais próximo
		// (o mais próximo fica por cima, como nos sprites ordenados pelo three).
		sortLabels() {
			const batch = this._labelBatch;
			if (!batch) return;
			const cam = this.camera.position;
			const visivelCache = new Map();
			const visivel = (obj) => {
				if (!visivelCache.has(obj)) {
					let v = true;
					for (let o = obj; o; o = o.parent) if (!o.visible) { v = false; break; }
					visivelCache.set(obj, v);
				}
				return visivelCache.get(obj);
			};
			let n = 0;
			batch.itens.forEach((it, i) => {
				if (!visivel(it.owner)) return;
				batch.dist[i] = it.anchor.distanceToSquared(cam);
				batch.ordem[n++] = i;
			});
			const ordem = batch.ordem.slice(0, n).sort((a, b) => batch.dist[b] - batch.dist[a]);
			const idx = batch.mesh.geometry.index;
			for (let k = 0; k < n; k++) {
				const v = ordem[k] * 4;
				idx.array.set([v, v + 1, v + 2, v, v + 2, v + 3], k * 6);
			}
			idx.needsUpdate = true;
			batch.mesh.geometry.setDrawRange(0, n * 6);
		},

		roundRectPath(ctx, x, y, w, h, r) {
			ctx.moveTo(x + r, y);
			ctx.lineTo(x + w - r, y);
			ctx.arc(x + w - r, y + r, r, -Math.PI / 2, 0);
			ctx.lineTo(x + w, y + h - r);
			ctx.arc(x + w - r, y + h - r, r, 0, Math.PI / 2);
			ctx.lineTo(x + r, y + h);
			ctx.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI);
			ctx.lineTo(x, y + r);
			ctx.arc(x + r, y + r, r, Math.PI, Math.PI * 1.5);
			ctx.closePath();
		},

		// ---------------------------------------------------------------
		// Cores / labels de temperatura
		// ---------------------------------------------------------------
		corPorTemp(temp) {
			const t = Number(temp);
			if (Number.isNaN(t)) return '#ffffff';
			const faixas = Object.keys(temperaturaCores).map(Number).sort((a, b) => a - b);
			for (const f of faixas) {
				if (t <= f) return temperaturaCores[f];
			}
			return '#ffffff';
		},

		tempLabel(temp) {
			const t = Number(temp);
			if (Number.isNaN(t)) return '--';
			return t < 99 ? String(temp) : 'OFF';
		},

		// ---------------------------------------------------------------
		// Toggles / reset
		// ---------------------------------------------------------------
		toggleArco(arco) {
			const grp = this.arcoGroups[arco];
			if (grp) {
				grp.visible = this.arcosVisiveis[arco];
				this.requestRender();
			}
		},
		toggleCaboCentral() {
			if (this.caboCentralGroup) {
				this.caboCentralGroup.visible = this.caboCentralVisivel;
				this.requestRender();
			}
		},
		toggleNivel() {
			if (this.nivelGroup) {
				this.nivelGroup.visible = this.nivelVisivel;
				this.requestRender();
			}
		},
		toggleValores() {
			(this._labelGroups || []).forEach(l => { l.visible = this.valoresVisiveis; });
			(this._dotGroups || []).forEach(d => { d.visible = !this.valoresVisiveis; });
			this.requestRender();
		},
		// Câmera na mesma distância/altura padrão, girada para ficar de frente para a escada.
		posicaoInicialCamera() {
			const padrao = new THREE.Vector3(4.4, 2.5, 5.8);
			if (this._anguloEscada == null) return padrao;
			const APROXIMACAO = 0.85;   // < 1 aproxima a câmera
			const GIRO = -0.25;         // rad; negativo gira a câmera para a direita (silo vai para a esquerda)
			const d = Math.hypot(padrao.x, padrao.z) * APROXIMACAO;
			const a = this._anguloEscada + GIRO;
			return new THREE.Vector3(d * Math.cos(a), padrao.y * APROXIMACAO, d * Math.sin(a));
		},
		resetSilo() {
			this.animarPara(this.posicaoInicialCamera(), new THREE.Vector3(0, 0.15, 0));
		},

		verificarForaDaTela() {
			if (!this._siloBox || this._siloBox.isEmpty()) return;
			this.camera.updateMatrixWorld();
			const frustum = new THREE.Frustum().setFromProjectionMatrix(
				new THREE.Matrix4().multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse)
			);
			if (!frustum.intersectsBox(this._siloBox)) this.resetSilo();
		},

		// Transição suave da câmera (e do alvo) até a posição desejada.
		animarPara(posFinal, alvoFinal, duracao = 450) {
			this.cancelarAnimacao();
			const posIni = this.camera.position.clone();
			const alvoIni = this.controls.target.clone();
			const inicio = performance.now();
			const passo = (agora) => {
				const t = Math.min(1, (agora - inicio) / duracao);
				const e = 1 - Math.pow(1 - t, 3);
				this.camera.position.lerpVectors(posIni, posFinal, e);
				this.controls.target.lerpVectors(alvoIni, alvoFinal, e);
				this.controls.update();
				this._animId = t < 1 ? requestAnimationFrame(passo) : null;
			};
			this._animId = requestAnimationFrame(passo);
		},

		cancelarAnimacao() {
			if (this._animId) cancelAnimationFrame(this._animId);
			this._animId = null;
		}
	}
};
</script>

<style scoped>
#scene {
	display: block;
	height: 60vh;
	width: 100%;
	min-height: 320px;
	cursor: grab;
}

#scene:active {
	cursor: grabbing;
}
</style>
